import { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  Path,
  SubmitHandler,
  UseFormReturn
} from 'react-hook-form';
import { RenderNewUploadingImagesList } from '@app/components/CreateEditCommon';
import { allowFiles } from '@app/helpers/uploadFileValidation';
import Radio from 'common/Radio';
import { BrandsEnum, VisibleEnum } from 'types';

import {
  FormSection,
  Header,
  LanguageBadge,
  LanguageColumn,
  LanguageGrid,
  LanguageTitle,
  Page,
  SectionHeader,
  SettingGroup,
  SettingsBox,
  StyledFormHelperText,
  UploadBox,
  VisuallyHiddenInput,
} from './styled';

interface ProductFormFields {
  titleAm?: string;
  descriptionAm?: string;
  titleEn?: string;
  descriptionEn?: string;
  titleRu?: string;
  descriptionRu?: string;
  images?: unknown;
  brand?: string;
}

interface ProductFormPageProps<TForm extends FieldValues, TState> {
  title: string;
  submitLabel: string;
  submittingLabel: string;
  methods: UseFormReturn<TForm>;
  previews: string[];
  setState: Dispatch<SetStateAction<TState>>;
  isSubmitting: boolean;
  isSuccess: string;
  largeImageMessage: string;
  existingImages?: ReactNode;
  onSubmit: SubmitHandler<TForm>;
  onChangeUpload: (event: ChangeEvent<HTMLInputElement>) => void;
}

const languageFields = [
  { code: 'AM', label: 'Armenian', title: 'titleAm', description: 'descriptionAm' },
  { code: 'EN', label: 'English', title: 'titleEn', description: 'descriptionEn' },
  { code: 'RU', label: 'Russian', title: 'titleRu', description: 'descriptionRu' },
] as const;

const ProductFormPage = <TForm extends FieldValues, TState>({
  title,
  submitLabel,
  submittingLabel,
  methods,
  previews,
  setState,
  isSubmitting,
  isSuccess,
  largeImageMessage,
  existingImages,
  onSubmit,
  onChangeUpload,
}: ProductFormPageProps<TForm, TState>) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const formErrors = errors as FieldErrors<ProductFormFields>;

  return (
    <FormProvider {...methods}>
      <Page>
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            '.MuiFormHelperText-root': { mx: 0 },
            '.MuiOutlinedInput-root': { background: '#fff' },
          }}
        >
          <Header>
            <Typography variant="h4" component="h1" fontWeight={600}>
              {title}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
              Complete the product information in all available languages.
            </Typography>
          </Header>

          <FormSection sx={{ pt: 0 }}>
            <SectionHeader>
              <Typography variant="h6" fontWeight={600}>Product information</Typography>
              <Typography variant="body2" color="text.secondary">
                Product name is required. Descriptions can contain up to 250 characters.
              </Typography>
            </SectionHeader>

            <LanguageGrid>
              {languageFields.map((language) => (
                <LanguageColumn key={language.code}>
                  <LanguageTitle>
                    <Typography fontWeight={600}>{language.label}</Typography>
                    <LanguageBadge>{language.code}</LanguageBadge>
                  </LanguageTitle>
                  <TextField
                    {...register(language.title as Path<TForm>)}
                    label={`Title ${language.code}`}
                    required
                    fullWidth
                    error={!!formErrors[language.title]}
                    helperText={formErrors[language.title]?.message as string}
                  />
                  <TextField
                    {...register(language.description as Path<TForm>)}
                    label={`Description ${language.code}`}
                    placeholder={`Enter ${language.label.toLowerCase()} description`}
                    multiline
                    rows={9}
                    fullWidth
                    error={!!formErrors[language.description]}
                    helperText={formErrors[language.description]?.message as string}
                  />
                </LanguageColumn>
              ))}
            </LanguageGrid>
          </FormSection>

          <FormSection>
            <SectionHeader>
              <Typography variant="h6" fontWeight={600}>Product images</Typography>
              <Typography variant="body2" color="text.secondary">
                Upload up to 5 images. Click a preview to view it.
              </Typography>
            </SectionHeader>

            {existingImages}
            <RenderNewUploadingImagesList<TState>
              newPreviewsList={previews}
              togetherImagesLength={previews.length}
              setState={setState}
            />

            <UploadBox>
              <Box>
                <Typography fontWeight={600}>Upload product images</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select one or multiple supported image files.
                </Typography>
              </Box>
              <Button
                component="label"
                role={undefined}
                variant="outlined"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                sx={{ flexShrink: 0, px: 2.5, textTransform: 'none' }}
              >
                Upload images
                <VisuallyHiddenInput
                  type="file"
                  id="files"
                  name="images"
                  multiple
                  accept={allowFiles.join(', ')}
                  onChange={onChangeUpload}
                />
              </Button>
            </UploadBox>
            {largeImageMessage && <StyledFormHelperText>{largeImageMessage}</StyledFormHelperText>}
            {formErrors.images?.message && (
              <StyledFormHelperText>{formErrors.images.message as string}</StyledFormHelperText>
            )}
          </FormSection>

          <FormSection>
            <SectionHeader>
              <Typography variant="h6" fontWeight={600}>Publishing settings</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose product visibility and brand before saving it.
              </Typography>
            </SectionHeader>
            <SettingsBox>
              <SettingGroup>
                <Typography fontWeight={600}>Visibility on website</Typography>
                <Radio label="Yes" name="visible" canBe={VisibleEnum.YES} />
                <Radio label="No" name="visible" canBe={VisibleEnum.NO} />
              </SettingGroup>
              <SettingGroup>
                <Typography fontWeight={600}>Brand</Typography>
                <Radio label="Finny" name="brand" canBe={BrandsEnum.FINNY} />
                <Radio label="Valio" name="brand" canBe={BrandsEnum.VALIO} />
                {formErrors.brand?.message && (
                  <StyledFormHelperText>{formErrors.brand.message as string}</StyledFormHelperText>
                )}
              </SettingGroup>
            </SettingsBox>
          </FormSection>

          {isSuccess && (
            <Alert severity={isSuccess.toLowerCase().includes('success') ? 'success' : 'error'} sx={{ mt: 3 }}>
              {isSuccess}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress color="inherit" size={18} /> : undefined}
            sx={{ mt: 3, py: 1.35, textTransform: 'uppercase' }}
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        </Stack>
      </Page>
    </FormProvider>
  );
};

export default ProductFormPage;
